import type { AABB, Vec2 } from '../../geo/types';
import { lotSides, type LotSide } from './lotFrame';
import type { Lot } from './types';
import { createCellGrid } from './cellGrid';

/** A stretch from the back corner in local plot-depth metres (normal to its street front). */
export type Stretch = readonly [number, number];

/** Which side walls of a lot, named as seen from its street, are shared with a neighbour. */
export interface PartyWalls {
  readonly left: boolean;
  readonly right: boolean;
  /**
   * Where other side walls lie against each side, however short. Measured from the back
   * corner, where a house's side wall ends whatever stands in front of its body.
   */
  readonly covered: { readonly left: readonly Stretch[]; readonly right: readonly Stretch[] };
}

/** STYLE §8.1: another house's side wall this close, this parallel and sharing this much length. */
export const PARTY_WALL = { maxGap: 0.15, maxAngle: (5 * Math.PI) / 180, minOverlap: 2 } as const;

// About the depth of a lot, so a side wall lands in two or three cells.
const HASH_CELL = 8;
const COS_MAX_ANGLE = Math.cos(PARTY_WALL.maxAngle);

interface Wall {
  readonly owner: number;
  readonly side: LotSide;
  readonly dir: Vec2;
}

function wallOf(owner: number, side: LotSide): Wall {
  const dx = side.to[0] - side.from[0];
  const dy = side.to[1] - side.from[1];
  const length = Math.hypot(dx, dy);
  return { owner, side, dir: [dx / length, dy / length] };
}

function boxOf(side: LotSide, grow: number): AABB {
  return {
    minX: Math.min(side.from[0], side.to[0]) - grow,
    minY: Math.min(side.from[1], side.to[1]) - grow,
    maxX: Math.max(side.from[0], side.to[0]) + grow,
    maxY: Math.max(side.from[1], side.to[1]) + grow,
  };
}

function along(p: Vec2, dir: Vec2): number {
  return p[0] * dir[0] + p[1] * dir[1];
}

function across(p: Vec2, dir: Vec2): number {
  return p[1] * dir[0] - p[0] * dir[1];
}

/** Offset across `dir` of the line through a wall, at `t` along `dir`. */
function acrossAt(side: LotSide, dir: Vec2, t: number): number {
  const [t0, t1] = [along(side.from, dir), along(side.to, dir)];
  const [c0, c1] = [across(side.from, dir), across(side.to, dir)];
  return c0 + ((c1 - c0) * (t - t0)) / (t1 - t0);
}

/**
 * The stretch of `a` that `b` lies against, or null. Measured along the mean direction of the
 * two walls, so the verdict is the same from either house and neighbours never disagree about
 * the wall they share.
 */
function contact(a: Wall, b: Wall): Stretch | null {
  const cos = a.dir[0] * b.dir[0] + a.dir[1] * b.dir[1];
  if (Math.abs(cos) < COS_MAX_ANGLE) return null;
  const s = cos < 0 ? -1 : 1;
  const mean: Vec2 = [a.dir[0] + s * b.dir[0], a.dir[1] + s * b.dir[1]];
  const length = Math.hypot(mean[0], mean[1]);
  const dir: Vec2 = [mean[0] / length, mean[1] / length];
  const [a0, a1] = [along(a.side.from, dir), along(a.side.to, dir)];
  const [b0, b1] = [along(b.side.from, dir), along(b.side.to, dir)];
  const lo = Math.max(Math.min(a0, a1), Math.min(b0, b1));
  const hi = Math.min(Math.max(a0, a1), Math.max(b0, b1));
  if (hi <= lo) return null;
  const gap = (t: number) => Math.abs(acrossAt(b.side, dir, t) - acrossAt(a.side, dir, t));
  if (gap(lo) > PARTY_WALL.maxGap || gap(hi) > PARTY_WALL.maxGap) return null;
  const [near, far] = [Math.abs(a1 - lo), Math.abs(a1 - hi)];
  return [Math.min(near, far), Math.max(near, far)];
}

function isParty(stretch: Stretch): boolean {
  return stretch[1] - stretch[0] >= PARTY_WALL.minOverlap;
}

/** Party walls of every lot, aligned with `lots`. */
export function partyWallsOf(lots: readonly Lot[]): readonly PartyWalls[] {
  const walls: Wall[] = [];
  lots.forEach((lot, owner) => {
    const { left, right } = lotSides(lot);
    walls.push(wallOf(owner, left), wallOf(owner, right));
  });
  const grid = createCellGrid<number>(HASH_CELL);
  walls.forEach((wall, i) => {
    grid.insert(i, boxOf(wall.side, PARTY_WALL.maxGap));
  });
  /* A wall filed in several cells is met once per cell; the first meeting counts, in filing order. */
  const metBy = new Int32Array(walls.length).fill(-1);
  const covered = walls.map((wall, w) => {
    const stretches: Stretch[] = [];
    grid.some(boxOf(wall.side, 0), (i) => {
      if (metBy[i] === w) return false;
      metBy[i] = w;
      const other = walls[i]!;
      const stretch = other.owner === wall.owner ? null : contact(wall, other);
      if (stretch) stretches.push(stretch);
      return false;
    });
    return stretches;
  });
  return lots.map((_, i) => {
    const scale = 1 / Math.hypot(1, lots[i]!.plot?.shear ?? 0);
    const local = (stretch: Stretch): Stretch => [stretch[0] * scale, stretch[1] * scale];
    const [left, right] = [covered[2 * i]!, covered[2 * i + 1]!];
    return {
      left: left.some(isParty), right: right.some(isParty),
      covered: { left: left.map(local), right: right.map(local) },
    };
  });
}
